import { useQuery } from '@tanstack/react-query';
import {
  AlertCircle,
  ArrowDown,
  ArrowUp,
  ChartNoAxesColumn,
  Download,
  Users,
} from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { Explain } from '@/components/Explain';
import { CopyId } from '@/components/primitives';
import { cn } from '@/lib/cn';
import type { Bootstrap } from '@/lib/bootstrap';
import type { TimeZoneMode } from '@/lib/format';
import type { PlayerReportInput, PlayerReportSort } from '@/types';
import { ApiError, fetchClients } from '@/features/bet-inspection/lib/api';
import { ReportFilterBar, type ReportFilters } from './components/ReportFilterBar';
import { fetchPlayerReport, fetchReportCurrencies } from './lib/api';
import { downloadCsv, reportFilename, toCsv } from './lib/csv';
import { defaultRange, toClickhouseUtc } from './lib/time';

/**
 * Player Reports — per-player bet / win / GGR for a period.
 *
 * Reads the ClickHouse rollups (sql/clickhouse/player_stats.sql). GGR is
 * bet − win − rollback and hold is ggr / turnover, identically for the settled
 * and live sources, so the two are directly comparable.
 *
 * All timestamps are UTC, like every other number in the console.
 */
export function PlayerReports({
  boot,
  tz,
  base,
}: {
  boot: Bootstrap;
  tz: TimeZoneMode;
  /** Global base currency — only used when no single currency is filtered. */
  base: string;
}) {
  const [filters, setFilters] = useState<ReportFilters>(defaultFilters);
  const [applied, setApplied] = useState<ReportFilters | null>(null);
  const [sort, setSort] = useState<PlayerReportSort>('bet');
  const [dir, setDir] = useState<'asc' | 'desc'>('desc');

  const clients = useQuery({
    queryKey: ['clients', boot.api],
    queryFn: ({ signal }) => fetchClients({ api: boot.api!, token: boot.token, signal }),
    enabled: Boolean(boot.api),
    staleTime: 5 * 60_000,
    retry: 1,
  });

  const currencies = useQuery({
    queryKey: ['report-currencies', boot.api, filters.psid],
    queryFn: ({ signal }) =>
      fetchReportCurrencies({
        api: boot.api!,
        token: boot.token,
        playServiceId: filters.psid,
        signal,
      }),
    enabled: Boolean(boot.api && filters.psid),
    staleTime: 5 * 60_000,
  });

  // Sorting re-queries: the server orders and paginates, so the page can't sort
  // a partial set and call it the top N.
  // `base` is part of the key: changing it re-queries — but only matters when no
  // single currency is filtered (the server ignores it otherwise).
  const report = useQuery({
    queryKey: ['player-report', boot.api, applied, sort, dir, base],
    queryFn: ({ signal }) =>
      fetchPlayerReport({
        api: boot.api!,
        token: boot.token,
        input: { ...toInput(applied!, base), sort, dir },
        signal,
      }),
    enabled: Boolean(boot.api && applied),
  });

  const rows = report.data?.rows ?? [];
  const totals = report.data?.totals;

  const csv = useMemo(() => (rows.length ? toCsv(rows) : ''), [rows]);

  // Exports are named by the project's NAME, not its opaque play_service_id.
  // Falls back to the id only if the client list hasn't resolved it.
  const clientName = useMemo(
    () =>
      (applied &&
        (clients.data ?? []).find((c) => c.play_service_id === applied.psid)?.client_name) ||
      applied?.psid ||
      '',
    [clients.data, applied],
  );

  return (
    <>
      <div className="sticky top-[41px] z-30 border-b border-hairline bg-bg/95 backdrop-blur">
        <ReportFilterBar
          value={filters}
          onChange={(patch) => setFilters((f) => ({ ...f, ...patch }))}
          onSearch={() => setApplied({ ...filters })}
          onReset={() => {
            setFilters(defaultFilters());
            setApplied(null);
          }}
          loading={report.isFetching}
          clients={clients.data ?? []}
          clientsLoading={clients.isLoading}
          clientsError={clients.isError}
          currencies={currencies.data ?? []}
          tz={tz}
          base={base}
        />
      </div>

      {report.isError && <Failure error={report.error as Error} />}
      {!applied && !report.isError && <Idle />}

      {applied && !report.isError && (
        <main className="rise">
          <KpiRow
            totals={totals}
            currency={report.data?.unit ?? applied.currency}
            source={report.data?.source ?? 'settled'}
            converted={report.data?.converted ?? false}
            loading={report.isFetching}
          />

          {/* Converted figures exclude any currency we hold no rate for — the
              reader has to know the total is short, not just see a number. */}
          {report.data?.unconverted?.length ? (
            <div className="flex items-center gap-2 border-b border-hairline bg-warn-bg/40 px-4 py-1.5 text-[12px] text-text-dim">
              <span className="text-warn">▲</span>
              Excluded from these totals — no exchange rate:{' '}
              <span className="font-mono text-warn">{report.data.unconverted.join(', ')}</span>
            </div>
          ) : null}

          <section className="border-b border-hairline">
            <div className="flex items-center gap-3 px-4 py-2">
              <span className="font-condensed text-[12px] font-semibold uppercase tracking-wider text-text">
                Players
              </span>
              <span className="font-mono text-[11px] text-text-dim">
                {report.isFetching ? 'loading…' : `${rows.length} shown`}
              </span>
              <button
                type="button"
                disabled={!csv}
                onClick={() =>
                  downloadCsv(
                    reportFilename(
                      clientName,
                      report.data?.unit ?? applied.currency,
                      toClickhouseUtc(applied.from),
                    ),
                    csv,
                  )
                }
                className="ml-auto inline-flex items-center gap-1.5 border border-hairline px-2.5 py-1 font-condensed text-[10px] font-semibold uppercase tracking-wider text-text-dim hover:border-hairline-strong hover:text-text disabled:opacity-40"
              >
                <Download size={12} /> Export CSV
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full border-collapse whitespace-nowrap text-[12px]">
                <thead>
                  <tr className="border-b border-hairline bg-surface-sunken">
                    <Th col="player_id" sort={sort} dir={dir} onSort={setSort} onDir={setDir} align="left">
                      <Explain term="player_id">Player</Explain>
                    </Th>
                    <Th col="bet" sort={sort} dir={dir} onSort={setSort} onDir={setDir}>Bet</Th>
                    <Th col="win" sort={sort} dir={dir} onSort={setSort} onDir={setDir}>Win</Th>
                    <Th col="ggr" sort={sort} dir={dir} onSort={setSort} onDir={setDir}>GGR</Th>
                    <Th col="hold" sort={sort} dir={dir} onSort={setSort} onDir={setDir}>Hold %</Th>
                    <Th col="bets" sort={sort} dir={dir} onSort={setSort} onDir={setDir}>Bets</Th>
                    <Th col="rounds" sort={sort} dir={dir} onSort={setSort} onDir={setDir}>Rounds</Th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.player_id} className="border-b border-hairline/50 last:border-0 hover:bg-surface-raised/50">
                      <td className="px-3 py-1.5">
                        <CopyId value={r.player_id} />
                      </td>
                      <td className="px-3 py-1.5 text-right"><Amount value={r.bet} /></td>
                      <td className="px-3 py-1.5 text-right"><Amount value={r.win} /></td>
                      <td className="px-3 py-1.5 text-right">
                        <Amount value={r.ggr} tone={Number(r.ggr) < 0 ? 'warn' : 'ok'} />
                      </td>
                      <td className="px-3 py-1.5 text-right tnum font-mono text-text-dim">
                        {formatPct(r.hold)}
                      </td>
                      <td className="px-3 py-1.5 text-right tnum font-mono text-text">{r.bets}</td>
                      <td className="px-3 py-1.5 text-right tnum font-mono text-text">{r.rounds}</td>
                    </tr>
                  ))}
                  {!rows.length && !report.isFetching && (
                    <tr>
                      <td colSpan={7} className="px-3 py-6 text-center text-[12px] text-text-dim">
                        No player activity for this project, currency and period.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <footer className="px-4 py-6 text-center text-[11px] text-text-faint">
            ggr = bet − win − rollback · hold = ggr / (bet − rollback) · times are UTC
          </footer>
        </main>
      )}
    </>
  );
}

function KpiRow({
  totals,
  currency,
  source,
  converted,
  loading,
}: {
  totals?: { players: number; bet: string; win: string; ggr: string; hold: number; bets: number; rounds: number };
  currency: string;
  source: 'settled' | 'live';
  converted: boolean;
  loading: boolean;
}) {
  return (
    <section className="border-b border-hairline bg-surface">
      <div className="flex flex-wrap items-stretch divide-x divide-hairline">
        <Kpi label="Players" icon={<Users size={12} />} value={totals ? String(totals.players) : '—'} sub="matched by filters" loading={loading} />
        <Kpi
          label="Total Bet"
          value={totals ? formatAmount(totals.bet) : '—'}
          sub={totals ? `${totals.bets} bets · ${totals.rounds} rounds` : ''}
          currency={currency}
          loading={loading}
        />
        <Kpi label="Total Win" value={totals ? formatAmount(totals.win) : '—'} sub="player payouts" currency={currency} loading={loading} />
        <Kpi
          label="GGR"
          icon={<ChartNoAxesColumn size={12} />}
          value={totals ? formatAmount(totals.ggr) : '—'}
          sub={totals ? `${formatPct(totals.hold)} hold` : ''}
          currency={currency}
          loading={loading}
        />
        <div className="ml-auto flex items-center gap-1.5 px-4">
          {converted && (
            <span
              title={`All currencies converted to ${currency} using the platform's exchange rates — approximate`}
              className="inline-flex items-center border border-info/40 bg-info-bg px-2 py-0.5 font-condensed text-[10px] font-semibold uppercase tracking-wider text-info"
            >
              fx → {currency}
            </span>
          )}
          <span
            title={
              source === 'live'
                ? 'Realtime mirror — current, but only successfully-processed transactions'
                : 'Settled rollup — authoritative, about an hour behind'
            }
            className={cn(
              'inline-flex items-center gap-1.5 border px-2 py-0.5 font-condensed text-[10px] font-semibold uppercase tracking-wider',
              source === 'live' ? 'border-warn/40 bg-warn-bg text-warn' : 'border-ok/40 bg-ok-bg text-ok',
            )}
          >
            {source}
          </span>
        </div>
      </div>
    </section>
  );
}

function Kpi({
  label,
  value,
  sub,
  currency,
  icon,
  loading,
}: {
  label: string;
  value: string;
  sub?: string;
  currency?: string;
  icon?: ReactNode;
  loading: boolean;
}) {
  return (
    <div className="min-w-[180px] px-4 py-2.5">
      <div className="eyebrow mb-1 flex items-center gap-1.5">
        {icon}
        {label}
      </div>
      <div className={cn('tnum font-mono text-[19px] font-semibold text-text', loading && 'opacity-50')}>
        {value}
        {currency && value !== '—' && (
          <span className="ml-1 text-[11px] font-normal text-text-faint">{currency}</span>
        )}
      </div>
      {sub && <div className="mt-0.5 text-[11px] text-text-dim">{sub}</div>}
    </div>
  );
}

function Th({
  col,
  sort,
  dir,
  onSort,
  onDir,
  align = 'right',
  children,
}: {
  col: PlayerReportSort;
  sort: PlayerReportSort;
  dir: 'asc' | 'desc';
  onSort: (c: PlayerReportSort) => void;
  onDir: (d: 'asc' | 'desc') => void;
  align?: 'left' | 'right';
  children: ReactNode;
}) {
  const active = sort === col;
  return (
    <th className={cn('px-3 py-1.5', align === 'right' ? 'text-right' : 'text-left')}>
      <button
        type="button"
        onClick={() => {
          if (active) onDir(dir === 'asc' ? 'desc' : 'asc');
          else {
            onSort(col);
            onDir('desc');
          }
        }}
        className={cn(
          'eyebrow inline-flex items-center gap-1 hover:text-text',
          active ? 'text-accent' : 'text-text-dim',
        )}
      >
        {children}
        {active ? (
          dir === 'asc' ? <ArrowUp size={10} /> : <ArrowDown size={10} />
        ) : (
          <span className="w-[10px]" />
        )}
      </button>
    </th>
  );
}

/** 2dp for reading, the exact stored value on hover — never destroy precision. */
function Amount({ value, tone }: { value: string; tone?: 'ok' | 'warn' }) {
  const n = Number(value);
  return (
    <span
      title={value}
      className={cn(
        'tnum cursor-help font-mono',
        tone === 'warn' ? 'text-warn' : tone === 'ok' ? 'text-text' : 'text-text',
      )}
    >
      {Number.isFinite(n) ? formatAmount(value) : value}
    </span>
  );
}

function formatAmount(value: string): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return value;
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatPct(hold: number): string {
  if (!Number.isFinite(hold)) return '—';
  return `${(hold * 100).toFixed(2)}%`;
}

/**
 * The query is always UTC. `currency` is only sent when one is chosen — omitting
 * it is what asks for all-currency, FX-converted figures. `base` is only sent in
 * that case, so a single-currency report is unaffected by the global base.
 */
function toInput(f: ReportFilters, base: string): PlayerReportInput {
  return {
    play_service_id: f.psid,
    ...(f.currency ? { currency: f.currency } : { base }),
    from: toClickhouseUtc(f.from),
    to: toClickhouseUtc(f.to),
    ...(f.playerId.trim() ? { player_id: f.playerId.trim() } : {}),
    ...(f.live ? { live: true } : {}),
    limit: 500,
  };
}

/** Default window: start of the current UTC month → the current hour. */
function defaultFilters(): ReportFilters {
  const { from, to } = defaultRange();
  return { psid: '', currency: '', from, to, playerId: '', live: false };
}

function Idle() {
  return (
    <div className="instrument-bg flex min-h-[40vh] flex-col items-center justify-center gap-3 px-6 text-center">
      <ChartNoAxesColumn size={22} className="text-text-faint" />
      <p className="max-w-[460px] text-[13px] leading-relaxed text-text-dim">
        Pick a project, currency and period to see per-player bets, wins and GGR. Times are UTC;
        the settled rollup runs about an hour behind, and the live toggle reads the realtime mirror.
      </p>
    </div>
  );
}

function Failure({ error }: { error: Error }) {
  const status = error instanceof ApiError ? error.status : 0;
  return (
    <div className="flex min-h-[30vh] items-start justify-center px-6 pt-12">
      <div className="flex max-w-[520px] gap-3 border border-crit/40 bg-crit-bg p-4">
        <AlertCircle size={16} className="mt-[2px] shrink-0 text-crit" />
        <div>
          <div className="mb-1 font-condensed text-[11px] font-semibold uppercase tracking-wider text-crit">
            {status === 403 ? 'Not permitted' : 'Report failed'}
          </div>
          <p className="text-[12.5px] leading-relaxed text-text">{error.message}</p>
        </div>
      </div>
    </div>
  );
}
